export const i18n = {
  defaultLocale: 'en',
  locales: ['en', 'de', 'pl', 'ru', 'es', 'sv', 'uk', 'ar', 'zh', 'he'],
  localeDetection: true,
} as const;

export type Locale = (typeof i18n)['locales'][number];

/** RTL languages. */
export const RTL_LOCALES = ['ar', 'he'];

/** 008.19: Check if a locale is Right-to-Left. */
export function isRtl(locale: string): boolean {
  return RTL_LOCALES.includes(locale);
}

/** BCP 47 locale for Intl (e.g. date formatting). en → en-GB; zh → zh-CN; others → xx-XX (e.g. ru-RU, de-DE). */
export function getDateLocale(locale: string): string {
  if (locale === 'en') return 'en-GB';
  if (locale === 'zh') return 'zh-CN';
  return `${locale}-${locale.toUpperCase()}`;
}

/**
 * Open Graph locale (Facebook/Meta underscore form).
 * Prefer real regional tags — avoid sv_SV / zh_ZH / uk_UK / he_HE.
 */
const OPEN_GRAPH_LOCALE: Record<string, string> = {
  en: 'en_US',
  de: 'de_DE',
  pl: 'pl_PL',
  ru: 'ru_RU',
  es: 'es_ES',
  sv: 'sv_SE',
  uk: 'uk_UA',
  ar: 'ar_SA',
  zh: 'zh_CN',
  he: 'he_IL',
};

/** Open Graph locale string (underscore: en_US, ru_RU, zh_CN…). */
export function getOpenGraphLocale(locale: string): string {
  return OPEN_GRAPH_LOCALE[locale] ?? `${locale}_${locale.toUpperCase()}`;
}

/** Other locales as og:locale:alternate (excludes the page locale). */
export function getOpenGraphAlternateLocales(locale: string): string[] {
  return i18n.locales
    .filter((loc) => loc !== locale)
    .map((loc) => getOpenGraphLocale(loc));
}

/** 008.19: Generate { locale: string } params for generateStaticParams. */
export function generateLocaleParams() {
  return i18n.locales.map((locale) => ({ locale }));
}

/** 008.19: Combine locale params with other path params. */
export function combineWithLocaleParams<T>(params: T[]) {
  const result: (T & { locale: Locale })[] = [];
  for (const locale of i18n.locales) {
    for (const p of params) {
      result.push({ ...p, locale });
    }
  }
  return result;
}
